import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Returns a "go back" handler that returns to the previous in-app page when there is one, and
 * otherwise navigates to `fallback`.
 *
 * Plain `navigate(-1)` does nothing useful when the user landed directly on the page (deep link or
 * refresh): a fresh history session has no in-app entry to return to, so the back button would
 * leave the app (or no-op). React Router marks that first entry with `location.key === "default"`,
 * which we use to fall back to a sensible overview route instead.
 */
export const useGoBack = (fallback: string): (() => void) => {
  const navigate = useNavigate();
  const { key } = useLocation();

  return useCallback(() => {
    if (key === "default") {
      navigate(fallback, { replace: true });
    } else {
      navigate(-1);
    }
  }, [navigate, key, fallback]);
};
